import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FilePenLine,
  Info,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useTranslation } from "../../../hooks/useTranslation";

// Dominio con TLD (dopo aver tolto l'eventuale protocollo)
const URL_RE = /^([\w-]+\.)+[a-z]{2,}([/?#].*)?$/i;

export const isValidWebsiteUrl = (value) =>
  URL_RE.test(value.trim().replace(/^https?:\/\//i, ""));

/**
 * Step "sito web" del ramo automatico (frame Figma 4002:6290 / 4010:7728
 * + stati dal frame errori 4002:7223): URL con prefisso https://, check
 * verde quando valido, CTA viola #AD46FF "Analizza sito web", banner
 * blu per passare alla configurazione manuale.
 */
const StepWebsite = ({
  url,
  onUrlChange,
  onBack,
  onAnalyze,
  onSwitchToCustom,
}) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  const cleaned = url.trim().replace(/^https?:\/\//i, "");
  const valid = URL_RE.test(cleaned);
  const invalid = touched && cleaned.length > 0 && !valid;

  return (
    <div className="mx-auto mt-[60px] flex w-full max-w-[640px] flex-col gap-5 px-4">
      <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
        <CardContent className="flex flex-col gap-8 p-6">
          <div>
            <h2 className="text-2xl font-semibold leading-6 text-[#020617]">
              {t("onboardingWizard.websiteTitle")}
            </h2>
            <p className="mt-2 text-sm tracking-[0.07px] text-[#737373]">
              {t("onboardingWizard.websiteSubtitle")}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-sm font-medium text-[#020617]">
                {t("onboardingWizard.websiteUrlLabel")}
              </Label>
              <div className="flex">
                <span className="flex items-center rounded-l-lg border border-r-0 border-[#CBD5E1] bg-[#F1F5F9] px-4 text-sm text-muted-foreground">
                  https://
                </span>
                <div className="relative flex-1">
                  <Input
                    className={`rounded-l-none rounded-r-lg border-[#CBD5E1] pr-9 text-sm font-medium ${
                      invalid
                        ? "border-destructive focus-visible:ring-destructive"
                        : ""
                    }`}
                    value={cleaned}
                    onChange={(e) => {
                      setTouched(true);
                      onUrlChange(e.target.value.replace(/^https?:\/\//i, ""));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && valid) {
                        e.preventDefault();
                        onAnalyze();
                      }
                    }}
                    placeholder="esempio.it"
                    spellCheck={false}
                  />
                  {valid && (
                    <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#19D163]" />
                  )}
                  {invalid && (
                    <TriangleAlert className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-destructive" />
                  )}
                </div>
              </div>
            </div>
            <p className="flex items-start gap-2 text-xs tracking-[0.06px] text-[#64748B]">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {t("onboardingWizard.websiteHint1")}
                <br />
                {t("onboardingWizard.websiteHint2")}
              </span>
            </p>
          </div>

          <div className="flex items-center justify-between pt-1">
            <Button type="button" variant="secondary" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("onboardingWizard.back")}
            </Button>
            <Button
              type="button"
              className="bg-[#AD46FF] px-6 text-[#F8FAFC] hover:bg-[#9a35eb] disabled:bg-slate-300 disabled:text-slate-500"
              disabled={!valid}
              onClick={onAnalyze}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {t("onboardingWizard.analyzeCta")}
            </Button>
          </div>

        </CardContent>
      </Card>

      {/* Banner per passare alla configurazione manuale (Sonner del design) */}
      <div className="flex w-full items-center gap-3 rounded-[10px] bg-[rgba(63,141,255,0.1)] p-4">
        <FilePenLine className="h-6 w-6 shrink-0 text-[#3F8DFF]" strokeWidth={1.75} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold tracking-[0.07px] text-[#020617]">
            {t("onboardingWizard.switchTitle")}
          </p>
          <p className="mt-1 text-xs leading-4 tracking-[0.18px] text-[#64748B]">
            {t("onboardingWizard.switchDesc")}
          </p>
        </div>
        <button
          type="button"
          onClick={onSwitchToCustom}
          className="flex h-6 shrink-0 items-center gap-2 rounded-lg bg-[rgba(191,222,255,0.7)] px-2 text-xs font-medium tracking-[0.18px] text-[#0F172A] transition-colors hover:bg-[rgba(191,222,255,0.95)]"
        >
          {t("onboardingWizard.switchCta")}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default StepWebsite;
